'use client'

import { AlertTriangle } from 'lucide-react'
import { DIRECT_STATE_CODES, NON_US_BUCKET_CODE, stateDisplayName } from '@/lib/constants'
import type { StateSegment } from '@/lib/types'

interface StateBreakdownProps {
	segments: StateSegment[]
	fuelType: 'diesel' | 'gas'
}

/**
 * Fixed grid column template shared by every row (header + data rows + Total).
 * Keeping this as a single constant guarantees numbers line up across
 * rows instead of drifting based on content width.
 *
 * Columns:
 *   [dot+code] [name flex] [miles] [price + source tag] [gallons] [cost]
 *
 * `minmax(0,1fr)` on the name column lets long names truncate with
 * ellipsis — without `minmax(0, ...)` the column expands to widest content
 * and blows out the grid.
 *
 * Sized by the CARD's width (container query on the section), not the
 * viewport: the card sits in a ~380px sidebar on desktop and ~358px on a
 * phone, and the six fixed columns need ~416px — the clipped column was COST,
 * the number that matters. Below a 512px card (`@lg`) the gallons column is
 * dropped (cells carry `GALLONS_CELL`).
 */
const ROW_GRID =
	'grid grid-cols-[1.5rem_minmax(0,1fr)_3.5rem_5rem_4.5rem] @lg:grid-cols-[2rem_minmax(0,1fr)_4rem_6rem_4rem_5rem] items-baseline gap-x-2 text-xs'

/** Gallons cells hide in a narrow card — see ROW_GRID. */
const GALLONS_CELL = 'hidden @lg:block text-right'

/**
 * Miles below this in the off-map ("Non-U.S.") bucket are treated as coastal
 * polygon-edge clipping — the relabeled row + tooltip already explain those.
 * Above it, the route is genuinely leaving the country (≈always Canada for
 * U.S. tour routing), worth a prominent note since those miles use the
 * national-average price rather than a real Canadian one.
 */
const CANADA_NOTE_MILES_THRESHOLD = 25

/** Tooltip copy for a row's price, explaining how granular the number is. */
function rowTitle(stateCode: string, name: string, isDirect: boolean): string {
	if (stateCode === NON_US_BUCKET_CODE) {
		return 'Miles outside U.S. states (e.g., a Canada crossing or coastal routing) — priced at the U.S. national-average fuel price.'
	}
	return isDirect
		? `${name}: EIA publishes weekly per-state prices.`
		: `${name}: EIA regional PADD average — shared with neighboring states in this district.`
}

/**
 * Price emphasis (cheapest/priciest highlight) for one row. Compares $/gal
 * across REAL STATES only — the Non-U.S. bucket is the flat national average,
 * not a state, so it never gets a "cheapest/priciest state" dot.
 */
function priceEmphasis(
	seg: StateSegment,
	maxPrice: number,
	minPrice: number,
	pricedCount: number,
): { dot: string | null; text: string } {
	if (seg.stateCode === NON_US_BUCKET_CODE || pricedCount <= 1) {
		return { dot: null, text: 'text-text-secondary' }
	}
	if (seg.fuelPrice === maxPrice) return { dot: 'bg-error-fg', text: 'text-error-fg' }
	if (seg.fuelPrice === minPrice) return { dot: 'bg-success', text: 'text-success' }
	return { dot: null, text: 'text-text-secondary' }
}

/** One data row in the breakdown table. */
function BreakdownRow({
	seg,
	maxPrice,
	minPrice,
	pricedCount,
}: {
	seg: StateSegment
	maxPrice: number
	minPrice: number
	pricedCount: number
}) {
	const isNonUs = seg.stateCode === NON_US_BUCKET_CODE
	const isDirect = DIRECT_STATE_CODES.has(seg.stateCode)
	const name = stateDisplayName(seg.stateCode)
	const tag = isNonUs ? 'AVG' : isDirect ? 'ST' : 'PADD'
	const { dot, text } = priceEmphasis(seg, maxPrice, minPrice, pricedCount)

	return (
		<div className={`${ROW_GRID} px-5 py-2.5 tabular-nums font-mono`}>
			{/* State code (or an em-dash for the off-map bucket) + price dot */}
			<span className="flex items-center gap-1.5">
				{dot && <span className={`size-1.5 rounded-full shrink-0 ${dot}`} />}
				<span className="text-[10px] uppercase tracking-[0.14em] text-text-muted">
					{isNonUs ? '—' : seg.stateCode}
				</span>
			</span>
			{/* Name */}
			<span className="text-sm text-text-primary truncate font-sans">{name}</span>
			{/* Miles */}
			<span className="text-right text-text-secondary">{seg.distanceMiles.toLocaleString()}</span>
			{/* Price with source tag (ST / PADD / AVG) */}
			<span className={`text-right ${text}`} title={rowTitle(seg.stateCode, name, isDirect)}>
				${seg.fuelPrice.toFixed(2)}
				<span className="ml-1.5 text-[9px] uppercase tracking-[0.14em] text-text-muted">{tag}</span>
			</span>
			{/* Gallons */}
			<span className={`${GALLONS_CELL} text-text-secondary`}>{seg.gallons.toLocaleString()}</span>
			{/* Cost */}
			<span className="text-right text-text-primary">${seg.cost.toFixed(2)}</span>
		</div>
	)
}

export function StateBreakdown({ segments, fuelType }: StateBreakdownProps) {
	if (segments.length === 0) return null

	const total = {
		distance: segments.reduce((s, seg) => s + seg.distanceMiles, 0),
		gallons: segments.reduce((s, seg) => s + seg.gallons, 0),
		cost: segments.reduce((s, seg) => s + seg.cost, 0),
	}

	// Cheapest/priciest highlight compares $/gal across real states only (see
	// priceEmphasis) — exclude the Non-U.S. average bucket from the min/max.
	const pricedSegments = segments.filter((s) => s.stateCode !== NON_US_BUCKET_CODE)
	const maxPrice = pricedSegments.length ? Math.max(...pricedSegments.map((s) => s.fuelPrice)) : 0
	const minPrice = pricedSegments.length ? Math.min(...pricedSegments.map((s) => s.fuelPrice)) : 0

	// Crosses-Canada note: only when the off-map portion is a genuine border
	// crossing (not a couple of coastal miles), since that's where the
	// national-average pricing meaningfully diverges from reality.
	const nonUs = segments.find((s) => s.stateCode === NON_US_BUCKET_CODE)
	const nonUsMiles = nonUs?.distanceMiles ?? 0
	const nonUsPct = total.distance > 0 ? Math.round((nonUsMiles / total.distance) * 100) : 0
	const showCanadaNote = nonUsMiles >= CANADA_NOTE_MILES_THRESHOLD

	return (
		<section className="@container rounded-2xl border border-border bg-surface overflow-hidden">
			{/* Header — eyebrow + fuel type meta */}
			<header className="flex items-end justify-between px-5 py-3 border-b border-border">
				<h3 className="font-mono text-[10px] uppercase tracking-[0.28em] text-text-muted">
					Per-State Breakdown
				</h3>
				<span className="font-mono text-[10px] uppercase tracking-[0.22em] text-text-muted">
					{fuelType}
				</span>
			</header>

			{/* Column headers */}
			<div
				className={`${ROW_GRID} px-5 py-2 border-b border-border font-mono text-[9px] uppercase tracking-[0.18em] text-text-muted`}
			>
				<span aria-hidden />
				<span>State</span>
				<span className="text-right">Miles</span>
				<span className="text-right">$/Gal</span>
				<span className={GALLONS_CELL}>Gallons</span>
				<span className="text-right">Cost</span>
			</div>

			{/* Rows */}
			<div className="divide-y divide-border">
				{segments.map((seg) => (
					<BreakdownRow
						key={seg.stateCode}
						seg={seg}
						maxPrice={maxPrice}
						minPrice={minPrice}
						pricedCount={pricedSegments.length}
					/>
				))}
			</div>

			{/* Total row — emphasized hairline + bold cost */}
			<div
				className={`${ROW_GRID} px-5 py-3 border-t border-border bg-background/40 tabular-nums font-mono`}
			>
				<span aria-hidden />
				<span className="font-mono text-[10px] uppercase tracking-[0.22em] text-text-muted">
					Total
				</span>
				<span className="text-right text-text-secondary">
					{Math.round(total.distance).toLocaleString()}
				</span>
				<span aria-hidden />
				<span className={`${GALLONS_CELL} text-text-secondary`}>
					{Math.round(total.gallons * 10) / 10}
				</span>
				<span className="text-right text-brand">${total.cost.toFixed(2)}</span>
			</div>

			{/* Crosses-Canada note — only fires on a real border crossing */}
			{showCanadaNote && (
				<output className="flex gap-2 border-t border-border bg-warning/10 px-5 py-2.5 text-[11px] leading-relaxed text-warning">
					<AlertTriangle className="size-3.5 shrink-0 mt-0.5" aria-hidden />
					<span>
						≈{Math.round(nonUsMiles).toLocaleString()} mi ({nonUsPct}%) of this route cross into
						Canada — those miles are priced at the U.S. national average, not a Canadian price.
					</span>
				</output>
			)}

			{/* Legend */}
			<div className="border-t border-border px-5 py-2.5 font-mono text-[9px] uppercase tracking-[0.18em] text-text-muted leading-relaxed">
				<span className="text-text-secondary">ST</span> · EIA weekly per-state price ·{' '}
				<span className="text-text-secondary">PADD</span> · Regional average shared by neighbors ·{' '}
				<span className="text-text-secondary">AVG</span> · U.S. national average for miles outside
				any state
			</div>
		</section>
	)
}
